import type { InputType } from 'config'
import {
  buyPricingFields,
  investorCommitmentFields,
  originatorLockFields,
  sellPricingFields,
} from 'pages/Loan/SecondaryMarketing/constant'

export type SmScreenSectionKey = 'originatorLock' | 'investorCommitment' | 'markup' | 'pricing'

export type SmTwoColumnGroup = 'leftGroup' | 'rightGroup'

export type SmFieldOrderMap = {
  originatorLock?: Partial<Record<SmTwoColumnGroup, string[]>>
  investorCommitment?: Partial<Record<SmTwoColumnGroup, string[]>>
  buyPricing?: string[]
  sellPricing?: string[]
}

export type ISmScreenFieldConfig = {
  title?: string
  visible?: boolean
}

export type ISmScreenSectionConfig = {
  title?: string
  visible?: boolean
}

export type ISmScreenConfig = {
  fields?: Record<string, ISmScreenFieldConfig>
  sections?: Record<string, ISmScreenSectionConfig>
  sectionOrder?: SmScreenSectionKey[]
  fieldOrder?: SmFieldOrderMap
}

export const SM_SCREEN_SECTIONS: Array<{ key: SmScreenSectionKey; title: string }> = [
  { key: 'originatorLock', title: 'Originator Lock Info' },
  { key: 'investorCommitment', title: 'Investor Commitment Info' },
  { key: 'markup', title: 'Markup' },
  { key: 'pricing', title: 'Pricing and Profit' },
]

export const defaultSmSectionOrder = (): SmScreenSectionKey[] => SM_SCREEN_SECTIONS.map((s) => s.key)

export const defaultSmFieldOrder = (): Required<SmFieldOrderMap> => ({
  originatorLock: {
    leftGroup: Object.keys(originatorLockFields.leftGroup),
    rightGroup: Object.keys(originatorLockFields.rightGroup),
  },
  investorCommitment: {
    leftGroup: Object.keys(investorCommitmentFields.leftGroup),
    rightGroup: Object.keys(investorCommitmentFields.rightGroup),
  },
  buyPricing: Object.keys(buyPricingFields),
  sellPricing: Object.keys(sellPricingFields),
})

export const emptySmScreenConfig = (): ISmScreenConfig => ({
  fields: {},
  sections: {},
  sectionOrder: [],
  fieldOrder: {},
})

export const normalizeSmScreenConfig = (raw?: ISmScreenConfig | null): ISmScreenConfig => ({
  fields: raw?.fields || {},
  sections: raw?.sections || {},
  sectionOrder: Array.isArray(raw?.sectionOrder) ? (raw!.sectionOrder as SmScreenSectionKey[]) : [],
  fieldOrder: raw?.fieldOrder || {},
})

export const getSmSectionTitle = (config: ISmScreenConfig, key: SmScreenSectionKey, fallback: string) =>
  config.sections?.[key]?.title || fallback

export const isSmSectionVisible = (config: ISmScreenConfig, key: SmScreenSectionKey) =>
  config.sections?.[key]?.visible !== false

export const getSmFieldTitle = (config: ISmScreenConfig, key: string, fallback: string) =>
  config.fields?.[key]?.title || fallback

export const isSmFieldVisible = (config: ISmScreenConfig, key: string) => config.fields?.[key]?.visible !== false

export const orderedSmSections = (config: ISmScreenConfig) => {
  const defaults = defaultSmSectionOrder()
  const saved = (config.sectionOrder || []).filter((k): k is SmScreenSectionKey => defaults.includes(k))
  const missing = defaults.filter((k) => !saved.includes(k))
  return [...saved, ...missing]
}

const mergeKeyOrder = (saved: string[] | undefined, defaults: string[]) => {
  const known = new Set(defaults)
  const ordered = (saved || []).filter((k) => known.has(k))
  defaults.forEach((k) => {
    if (!ordered.includes(k)) ordered.push(k)
  })
  return ordered
}

export const orderedTwoColumnKeys = (
  config: ISmScreenConfig,
  section: 'originatorLock' | 'investorCommitment',
  group: SmTwoColumnGroup,
  defaults: Record<string, InputType>,
) => mergeKeyOrder(config.fieldOrder?.[section]?.[group], Object.keys(defaults))

export const orderedFlatKeys = (
  config: ISmScreenConfig,
  section: 'buyPricing' | 'sellPricing',
  defaults: Record<string, InputType>,
) => mergeKeyOrder(config.fieldOrder?.[section], Object.keys(defaults))

export const getTwoColumnFieldDef = (
  map: Record<string, Record<string, InputType>>,
  group: SmTwoColumnGroup,
  key: string,
): InputType | undefined => map[group]?.[key]

export const SM_COLUMN_LABELS: Record<SmTwoColumnGroup, string> = {
  leftGroup: 'Left column',
  rightGroup: 'Right column',
}
